import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import {
  FileContainer as File,
  Spacer,
  Visible,
} from '@tfecom-thermofisher/react-komodo-design';

import { Title, UploadSingleFile } from 'src/core/components';
import { status as statusConstants } from 'src/constants';

import { selectStateData } from 'src/core/redux/selectors';
import { PLATE_SIZE_DETAILS_NAMESPACE } from 'src/constants/forms';
import {
  uploadFile as uploadFileAction,
  removeFileData,
  removePlate as removePlateAction,
  refreshPlates as refreshPlatesAction,
} from './actions';
import { selectFileData } from './selectors';
import {
  UploadedTemplateDetails,
  DownloadFilesList,
  Plates,
  PlateErrorAlert,
} from './components';

import messages from './messages';

import './UploadPlateTemplate.style.scss';

const UploadPlateTemplateContainer = ({
  file,
  formData,
  resetData,
  uploadFile,
  removePlate,
  refreshPlates,
}) => {
  const isFileUploaded = !!(file.uploaded && !file.errorMessages?.length);
  const plates = file.data?.plateStatuses || [];
  const hasPlateError = plates.find((plate) => !plate.isValid);
  const hasUploadedFileError = !!(
    file.status === statusConstants.ERROR || file.errorMessages?.length
  );
  const fileErrorMessages = file.errorMessages?.length ? file.errorMessages : [];

  React.useEffect(() => {
    refreshPlates();
  }, [refreshPlates]);

  React.useEffect(() => {
    if (isFileUploaded) {
      window.scrollTo(0, 0);
    }
  }, [isFileUploaded]);

  return (
    <>
      <Spacer top={32} bottom={24}>
        <Title
          size="4"
          font="bold"
          message={
            isFileUploaded ?
              messages.successUploadedSubTitle :
              messages.subTitle
          }
        />
      </Spacer>
      <div className="dna-uploader-container">
        <Visible when={!isFileUploaded}>
          <UploadSingleFile
            file={file}
            resetData={resetData}
            uploadFile={uploadFile}
          />
          <Visible when={isEmpty(file)}>
            <Spacer top={32} />
            <DownloadFilesList />
          </Visible>
        </Visible>
        <Visible when={isFileUploaded}>
          <File file={file} deleteFile={resetData} />
        </Visible>
      </div>
      <Visible when={isFileUploaded}>
        <UploadedTemplateDetails details={file.additionalData} formData={formData} />
        <Spacer top={32} />
        <Plates plates={plates} removePlate={removePlate} />
        <Spacer bottom={25} />
        <Visible when={hasPlateError}>
          <PlateErrorAlert message={messages.plateError} />
        </Visible>
      </Visible>
      <Visible when={hasUploadedFileError}>
        <Spacer bottom={25} />
        {fileErrorMessages?.map(
          (errorMessage) => <PlateErrorAlert key={errorMessage.id} message={errorMessage} />
        )}
      </Visible>
    </>
  );
};

UploadPlateTemplateContainer.propTypes = {
  file: PropTypes.object,
  formData: PropTypes.object,
  uploadFile: PropTypes.func.isRequired,
  removePlate: PropTypes.func.isRequired,
  resetData: PropTypes.func.isRequired,
  refreshPlates: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  file: selectFileData(state),
  formData: selectStateData(PLATE_SIZE_DETAILS_NAMESPACE)(state),
});

const mapDispatchToProps = {
  uploadFile: uploadFileAction,
  resetData: removeFileData,
  removePlate: removePlateAction,
  refreshPlates: refreshPlatesAction,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(UploadPlateTemplateContainer);
